/***
*
*   DASHBOARDS
*   Analytics hub — usage, feature adoption, org comparison, reliability
*
**********/

import { useState, useMemo } from 'react';
import { Animate, Card, Chart, Grid, Stat, Table, useAPI } from 'components/lib';

const PRESETS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y', days: 365 },
];

function toISO(date) {
  return date.toISOString().split('T')[0];
}

function buildDateParams(days) {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return `?from=${toISO(from)}&to=${toISO(to)}`;
}

function formatChange(value) {
  if (value === null || value === undefined) return undefined;
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${value}%`;
}

const sectionHeaderStyle = {
  fontSize: '20px',
  fontWeight: 700,
  color: '#111827',
  borderBottom: '2px solid #f3f4f6',
  paddingBottom: '12px',
  marginBottom: '20px',
  marginTop: '36px',
};

const firstSectionHeaderStyle = {
  ...sectionHeaderStyle,
  marginTop: '0',
};

// ---------------------------------------------------------------------------
// Section 1: Company-Wide Usage
// ---------------------------------------------------------------------------

function SectionUsage() {
  const [activeDays, setActiveDays] = useState(30);
  const params = useMemo(() => buildDateParams(activeDays), [activeDays]);

  const stats = useAPI(`/api/metrics/summary${params}`);
  const growth = useAPI(`/api/metrics/accounts/growth${params}`);

  // Prompt volume — use prompts series if present, fall back to accounts
  const promptChartData = useMemo(() => {
    const series = growth?.data?.prompts || growth?.data?.accounts;
    if (!series) return null;
    return series;
  }, [growth]);

  const usersChartData = useMemo(() => {
    return growth?.data?.users || null;
  }, [growth]);

  return (
    <div>
      <h2 style={firstSectionHeaderStyle}>Company-Wide Usage</h2>

      {/* Date preset buttons */}
      <div className='flex items-center gap-2 mb-6'>
        {PRESETS.map((p) => (
          <button
            key={p.label}
            onClick={() => setActiveDays(p.days)}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              activeDays === p.days
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Summary stats */}
      <Grid cols='4'>
        <Stat
          loading={stats?.loading}
          value={stats?.data?.totalUsers}
          label='Active Users'
          icon='users'
        />
        <Stat
          loading={stats?.loading}
          value={stats?.data?.newAccounts}
          label='Total Prompts'
          icon='message-square'
          change={formatChange(stats?.data?.newAccountsChange)}
        />
        <Stat
          loading={stats?.loading}
          value={
            stats?.data?.totalUsers && stats?.data?.newAccounts
              ? (stats.data.newAccounts / stats.data.totalUsers).toFixed(1)
              : undefined
          }
          label='Avg Prompts / User'
          icon='activity'
        />
        <Stat
          loading={stats?.loading}
          value={formatChange(stats?.data?.newAccountsChange)}
          label='Prompts Growth %'
          icon='trending-up'
        />
      </Grid>

      <Card title='Prompt Volume Over Time'>
        <Chart
          type='line'
          color='blue'
          data={promptChartData}
          loading={growth?.loading}
        />
      </Card>

      <Card title='Active Users Over Time'>
        <Chart
          type='line'
          color='green'
          data={usersChartData}
          loading={growth?.loading}
        />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 2: Feature Adoption
// ---------------------------------------------------------------------------

const FEATURE_STATS = [
  { key: 'document_upload_submitted', label: 'Doc Upload',       icon: 'file-text'    },
  { key: 'speech_to_text_used',       label: 'Voice to Text',    icon: 'mic'          },
  { key: 'email_ai_submitted',        label: 'Email AI',         icon: 'mail'         },
  { key: 'non_legal_submitted',       label: 'Non-Legal Queries',icon: 'alert-circle' },
];

function buildFeatureBarData(adoptionSummary) {
  if (!adoptionSummary || !adoptionSummary.length) return null;
  const relevant = FEATURE_STATS.map((f) => {
    const found = adoptionSummary.find((s) => s.name === f.key);
    return { label: f.label, count: found ? found.total : 0 };
  });
  return {
    labels: relevant.map((r) => r.label),
    datasets: [{ label: 'Events', data: relevant.map((r) => r.count) }],
  };
}

function SectionFeatureAdoption() {
  const [activeDays, setActiveDays] = useState(30);
  const params = useMemo(() => buildDateParams(activeDays), [activeDays]);

  // TODO: wire feature adoption metrics — using /api/insights/adoption for now
  const adoption = useAPI(`/api/insights/adoption${params}`);
  const adoptionSummary = adoption?.data?.adoptionSummary || [];

  const barData = useMemo(() => buildFeatureBarData(adoptionSummary), [adoptionSummary]);

  return (
    <div>
      <h2 style={sectionHeaderStyle}>Feature Adoption</h2>

      <div className='flex items-center gap-2 mb-6'>
        {PRESETS.map((p) => (
          <button
            key={p.label}
            onClick={() => setActiveDays(p.days)}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              activeDays === p.days
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <Grid cols='4'>
        {FEATURE_STATS.map((f) => {
          const found = adoptionSummary.find((s) => s.name === f.key);
          return (
            <Stat
              key={f.key}
              loading={adoption?.loading}
              value={found ? found.total : (adoption?.loading ? undefined : '\u2014')}
              label={f.label}
              icon={f.icon}
            />
          );
        })}
      </Grid>

      <Card title='Feature Usage Breakdown'>
        <Chart
          type='bar'
          color='blue'
          data={barData}
          loading={adoption?.loading}
        />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 3: Organisation Comparison
// ---------------------------------------------------------------------------

function SectionOrgComparison() {
  // TODO: add per-account metrics breakdown
  const accounts = useAPI('/api/account');

  const tableData = useMemo(() => {
    if (!accounts?.data) return null;
    const rows = Array.isArray(accounts.data) ? accounts.data : [];
    return rows.slice(0, 20).map((a) => ({
      name:             a.name || '—',
      users:            a.users ?? '—',
      plan:             a.plan || a.subscription_status || '—',
      subscription:     a.subscription_status || '—',
    }));
  }, [accounts]);

  return (
    <div>
      <h2 style={sectionHeaderStyle}>Organisation Comparison</h2>

      <Card title='Top Accounts by Usage'>
        <Table
          search
          sort
          show={['name', 'users', 'plan', 'subscription']}
          data={tableData}
          loading={accounts?.loading}
        />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 4: Error & Reliability (placeholder)
// ---------------------------------------------------------------------------

function SectionReliability() {
  // TODO: wire to monitoring/APM system
  return (
    <div>
      <h2 style={sectionHeaderStyle}>Error &amp; Reliability</h2>

      <Grid cols='3'>
        <Stat value='\u2014' label='Uptime'            icon='shield'       />
        <Stat value='\u2014' label='Error Rate'        icon='alert-triangle'/>
        <Stat value='\u2014' label='Avg Response Time' icon='clock'        />
      </Grid>

      <Card title='Error Trends'>
        <div
          style={{
            background: '#f9fafb',
            border: '2px dashed #d1d5db',
            borderRadius: '8px',
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9ca3af',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          Error trends &mdash; coming soon
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root export
// ---------------------------------------------------------------------------

export function Dashboards() {
  return (
    <Animate type='pop'>
      <SectionUsage />
      <SectionFeatureAdoption />
      <SectionOrgComparison />
      <SectionReliability />
    </Animate>
  );
}
